// Closing §review — a consolidated, read-only view of everything outside the
// Closing Details + Fees sections, shown to Closing before the order is sent to
// the LO (prototype ClosingReviewSummary; screenshot 09). Pure projection of the
// order + derived docs — no editing, no core rules beyond display formatting.
import { Fragment } from 'react';
import { Icon } from '../atoms';
import { useDocOrderCtx } from '../context';
import { fmtDate, fmtMoney, fmtStamp } from '../format';
import { DO_CONFIG, isCurrentFeeEscalation, type ChecklistDoc, type DocOrderFee } from '../core';
import type { DocOrderRow } from '@/services/docOrderService';
import type { buildLoanFacts } from '../viewModel';

interface ClosingReviewSummaryProps {
  order: DocOrderRow;
  fees: DocOrderFee[];
  docs: ChecklistDoc[];
  loanFacts: ReturnType<typeof buildLoanFacts>;
  stepNo: number;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rev-row">
      <span className="rev-lbl">{label}</span>
      <span className="rev-val">{value === 0 || value ? value : '—'}</span>
    </div>
  );
}

interface InsurancePolicy {
  label: string;
  carrier: string | null;
  premium: number | null;
  eff: string | null;
  exp: string | null;
  agent: string | null;
}

export function ClosingReviewSummary({ order, docs, loanFacts, fees = [], stepNo }: ClosingReviewSummaryProps) {
  const { now } = useDocOrderCtx();
  const s = order.settlement;
  const ins = order.insurance;
  const M = (n: number | null | undefined) => fmtMoney(n, true);
  const D = (d: string | null | undefined) => (d ? fmtDate(d) : '—');

  const policies: InsurancePolicy[] = [];
  if (ins && !ins.deferred) {
    if (ins.company) {
      policies.push({ label: 'Primary (HOI)', carrier: ins.company, premium: ins.premium, eff: ins.effective, exp: ins.expiration, agent: ins.email });
    }
    if (ins.dicCompany || ins.dicExtendedCoverages) {
      policies.push({ label: 'Secondary (DIC)', carrier: ins.dicCompany, premium: ins.dicPremium, eff: ins.dicEffective, exp: ins.dicExpiration, agent: ins.dicEmail });
    }
  }

  return (
    <section className="ws-section" id="sec-review">
      <div className="card card-pad snapshot-compact">
        <div className="ws-section-head">
          <h3 className="ws-section-title">
            <span className="ws-step-no">{stepNo}</span>Order Review
          </h3>
        </div>
        <div className="rev-grid">
          <div className="rev-group">
            <div className="rev-group-h">
              <Icon name="home" size={13} />Loan
            </div>
            <Row label="Borrower(s)" value={loanFacts.borrowers.join(', ')} />
            <Row label="Loan number" value={loanFacts.loanNumber} />
            <Row label="Product" value={loanFacts.product} />
            <Row label="Purpose" value={loanFacts.purpose} />
            <Row label="Loan amount" value={M(loanFacts.loanAmount)} />
            <Row label="Property" value={loanFacts.property} />
            <Row label="Occupancy" value={loanFacts.occupancy} />
            <Row label="Property type" value={loanFacts.propertyType} />
          </div>

          <div className="rev-group">
            <div className="rev-group-h">
              <Icon name="building" size={13} />Settlement &amp; Title
            </div>
            <Row label="Settlement agent" value={s?.agent} />
            <Row label="Contact" value={s?.contact} />
            <Row label="Email" value={s?.email} />
            <Row label="Phone" value={s?.phone} />
            <Row label="Loan docs email" value={order.doc_email} />
          </div>

          <div className="rev-group">
            <div className="rev-group-h">
              <Icon name="shield" size={13} />Insurance
            </div>
            {ins?.deferred ? (
              <Row label="Status" value="Deferred — binder pending" />
            ) : policies.length === 0 ? (
              <Row label="Carrier" value="—" />
            ) : (
              policies.map((p, i) => (
                <Fragment key={p.label}>
                  {policies.length > 1 && <div className="rev-subhead">{p.label}</div>}
                  <Row label="Carrier" value={p.carrier} />
                  <Row label="Annual premium" value={M(p.premium)} />
                  <Row label="Effective" value={D(p.eff)} />
                  <Row label="Expiration" value={D(p.exp)} />
                  <Row label="Agent" value={p.agent} />
                  {i < policies.length - 1 && <div className="rev-spacer" />}
                </Fragment>
              ))
            )}
          </div>

          <div className="rev-group rev-group-docs">
            <div className="rev-group-h">
              <Icon name="folder" size={13} />Closing Documents
            </div>
            {docs.length === 0 ? (
              <div className="rev-row">
                <span className="rev-lbl">No documents</span>
              </div>
            ) : (
              docs.map((d) => {
                let stale: string | null = null;
                if (d.id === 'd-cd' && d.receivedAt) {
                  const ageDays = Math.floor((now.getTime() - new Date(d.receivedAt).getTime()) / 86_400_000);
                  if (ageDays >= DO_CONFIG.essStaleDays) {
                    stale = `Stale · ${ageDays}d`;
                  }
                }
                // R5 #6b/#7c: the SAME structured-evidence labels as the
                // Documents section — never "Received —" on auto rows, never
                // "Received" off a 3977 sent-only clear.
                const meta =
                  d.status === 'received'
                    ? d.evidence?.source === 'cd_received'
                      ? `Received ${D(d.evidence.at)}`
                      : d.evidence?.source === 'cd_sent'
                        ? `Sent ${D(d.evidence.at)}`
                        : d.evidence?.source === 'piw-4954'
                          ? 'Cleared via PIW — Field 4954'
                          : d.evidence?.source === 'business-purpose'
                            ? `Waived — business purpose (GFE ${D(d.evidence.at)})`
                            : d.receivedAt
                              ? `Received ${D(d.receivedAt)}${d.file ? ` · ${d.file}` : ''}`
                              : d.auto?.source === 'piw-4954'
                                ? 'Cleared via PIW — Field 4954'
                                : d.auto
                                  ? 'Auto-cleared'
                                  : 'Received —'
                    : d.status === 'pending'
                      ? 'Pending'
                      : 'Missing';
                return (
                  <div key={d.id} className="rev-doc">
                    <span className={`rev-doc-ico ${d.status}`}>
                      <Icon name={d.status === 'received' ? 'fileCheck' : d.status === 'pending' ? 'clock' : 'alertTriangle'} size={14} />
                    </span>
                    <div className="rev-doc-main">
                      <span className="rev-doc-name">
                        {d.name}
                        {stale && (
                          <span className="rev-doc-stale">
                            <Icon name="alertTriangle" size={9} />
                            {stale}
                          </span>
                        )}
                      </span>
                      <span className="rev-doc-meta">{meta}</span>
                    </div>
                  </div>
                );
              })
            )}
            {/* R5 review-3 #5 — missing-invoice escalations are ADDRESSED to
                Closing: list each with its note/actor/stamp and the honest
                evidence state (the count above never claims an invoice). */}
            {(() => {
              const escalated = fees.filter(
                (f) => f.acks?.escalation?.kind === 'missing_invoice' && isCurrentFeeEscalation(f, f.acks.escalation)
              );
              if (escalated.length === 0) {
                return null;
              }
              return (
                <div className="invoice-escalated-list">
                  <div className="invoice-escalated-head">
                    <Icon name="flag" size={13} />
                    <span>{escalated.length} fee{escalated.length > 1 ? 's' : ''} resolved by missing-invoice escalation — no invoice in the eFolder.</span>
                  </div>
                  {escalated.map((f) => (
                    <div key={f.id} className="invoice-escalated-row">
                      <span className="inv-esc-fee">{f.name} · {fmtMoney(Number(f.amount) || 0, true)}</span>
                      <span className="inv-esc-note">&ldquo;{f.acks?.escalation?.note}&rdquo;</span>
                      <span className="inv-esc-stamp">{f.acks?.escalation?.by} · {fmtStamp(f.acks?.escalation?.at)}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </section>
  );
}
